import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { NeonText } from '../effects';
import { useLanguage } from '@/i18n';
import { experience, education } from '../../data';
import { FiChevronDown } from 'react-icons/fi';
import styles from './Experience.module.css';

interface TimelineAccordionProps {
  title: string;
  subtitle: string;
  period: string;
  badge?: string;
  children?: React.ReactNode;
  hasContent?: boolean;
}

function TimelineAccordion({ title, subtitle, period, badge, children, hasContent = true }: TimelineAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isMobile) {
    return (
      <div className={styles.timelineContent}>
        <div className={styles.timelineHeader}>
          <div>
            <h4 className={styles.role}>{title}</h4>
            <p className={styles.company}>
              {subtitle}
              {badge && <span className={styles.badge}>{badge}</span>}
            </p>
          </div>
          <span className={styles.period}>{period}</span>
        </div>
        {children}
      </div>
    );
  }

  // If no content to expand, show non-interactive version
  if (!hasContent) {
    return (
      <div className={`${styles.timelineContent} ${styles.timelineAccordion}`}>
        <div className={styles.timelineToggleStatic}>
          <div className={styles.timelineHeader}>
            <h4 className={styles.role}>{title}</h4>
            <p className={styles.company}>
              {subtitle}
              {badge && <span className={styles.badge}>{badge}</span>}
            </p>
          </div>
          <div className={styles.timelineHeaderRight}>
            <span className={styles.period}>{period}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.timelineContent} ${styles.timelineAccordion}`}>
      <button
        className={styles.timelineToggle}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.timelineHeader}>
          <h4 className={styles.role}>{title}</h4>
          <p className={styles.company}>
            {subtitle}
            {badge && <span className={styles.badge}>{badge}</span>}
          </p>
        </div>
        <div className={styles.timelineHeaderRight}>
          <span className={styles.period}>{period}</span>
          <FiChevronDown className={`${styles.toggleIcon} ${isExpanded ? styles.toggleIconOpen : ''}`} />
        </div>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className={styles.timelineBody}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Experience() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t, language } = useLanguage();

  // Helper function to translate endDate
  const translateEndDate = (endDate: string) => {
    if (endDate === 'Actualmente') {
      return t.experience.currently;
    }
    return endDate;
  };

  // Helper function to get badge text
  const getBadgeText = (type: string) => {
    if (type === 'founder') return t.experience.founder;
    if (type === 'partner') return t.experience.partner;
    return undefined;
  };

  return (
    <section id="my-resume" className={styles.experience} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <NeonText as="h2" color="cyan" effect="glow" className={styles.title} animate={false}>
            {t.experience.title}
          </NeonText>
        </motion.div>

        <div className={styles.content}>
          {/* Experience Column */}
          <motion.div
            className={styles.column}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className={styles.columnTitle}>{t.experience.myExperience}</h3>

            <div className={styles.timeline}>
              {experience.map((exp, index) => (
                <motion.div
                  key={exp.id}
                  className={styles.timelineItem}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <TimelineAccordion
                    title={exp.role}
                    subtitle={exp.company}
                    period={`${exp.startDate} - ${translateEndDate(exp.endDate)}`}
                    badge={getBadgeText(exp.type)}
                  >
                    <ul className={styles.descriptionList}>
                      {(language === 'en' && exp.descriptionEn ? exp.descriptionEn : exp.description).map((desc, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: desc }} />
                      ))}
                    </ul>
                  </TimelineAccordion>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Education Column */}
          <motion.div
            className={styles.column}
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className={styles.columnTitle}>{t.experience.myEducation}</h3>

            <div className={styles.timeline}>
              {education.map((edu, index) => (
                <motion.div
                  key={edu.id}
                  className={styles.timelineItem}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                >
                  <TimelineAccordion
                    title={language === 'en' && edu.titleEn ? edu.titleEn : edu.title}
                    subtitle={edu.institution}
                    period={edu.startDate === edu.endDate ? edu.startDate : `${edu.startDate} - ${edu.endDate}`}
                    hasContent={!!(edu.description || edu.descriptionEn)}
                  >
                    {(edu.description || edu.descriptionEn) && (
                      <p className={styles.eduDescription}>
                        {language === 'en' && edu.descriptionEn ? edu.descriptionEn : edu.description}
                      </p>
                    )}
                  </TimelineAccordion>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
